import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Typography,
  TextField,
  MenuItem,
  Accordion,
  AccordionDetails,
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormGroup,
  AccordionSummary,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import Grid2 from "@mui/material/Grid2";
import AuthContext from "../Context/AuthProvider";
import {
  addPrescription,
  updatePrescription,
  getPrescription,
  removePrescription,
} from "../API/PrescriptionApi";
import { getServiceDetails } from "../API/LinkedServicesApi";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import { formatAge } from "../Utility/Utility";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import MenuBar from "../common/MenuBar";
import {
  PrescriptionTypes,
  muscleOptions,
  romMovements,
  difficultyOptions,
  modalityOptions,
  exerciseOptions,
} from "../Utility/Constants";
import { getFrozenShoulderDataJson } from "./FrozenShoulderDataStructure";
import { useSnackbar } from "../Utility/UserMessageContext";
import NotesTextField from "../Worklist/NotesTextField";
import { showLoadingImage } from "../Utility/Utility";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FrozenShoulderPrescriptionReadOnly from "./FrozenShoulderPrescriptionReadOnly";

function FrozenShoulderPrescriptionForm({
  serviceId,
  uniqueId,
  prescriptionId,
  onSavePrescription,
  onRemovePrescription,
  changeTabsLabel,
  showLoading = false,
}) {
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  //const [prescriptionToBeEdited, setPrescriptionToBeEdited] = useState();

  // --- STATES ---
  const [currentService, setCurrentService] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [prescriptionStatus, setPrescriptionStatus] = useState("Draft");
  const [readOnly, setReadOnly] = useState(false);
  const [currentPrescriptionId, setCurrentPrescriptionId] =
    useState(prescriptionId);

  const [frozenShoulderData, setFrozenShoulderData] = useState(
    getFrozenShoulderDataJson(),
  );
  // Add after all useState declarations and before useEffect
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Prescription Status
  const [status, setStatus] = useState("Draft");

  const displayText = (value, fallback = "Not available") => {
    return value !== undefined && value !== null ? value : fallback;
  };

  useEffect(() => {
    const loadService = async () => {
      if (auth && serviceId) {
        setIsSaving(true);
        try {
          const result = await getServiceDetails(auth, serviceId);
          if (result?.isSuccess) {
            setCurrentService(result?.data);
          } else {
            showSnackbar(
              result?.data?.message ||
                JSON.stringify(result?.data) ||
                "An error occurred",
              "error",
            );
          }
        } catch (error) {
          showSnackbar(
            "Error loading service details: " + error.message,
            "error",
          );
        } finally {
          setIsSaving(false);
        }
      }
    };

    const loadPrescription = async () => {
      if (auth && currentPrescriptionId) {
        try {
          const result = await getPrescription(auth, currentPrescriptionId);
          if (result?.isSuccess) {
            const p = result.data;
            const details = JSON.parse(p?.detailedPrescription || "{}");
            changeTabsLabel && changeTabsLabel(uniqueId, `${p?.displayId}`); // Update tab label with prescription display ID
            //setCurrentPrescriptionId(p?.id);

            setFrozenShoulderData({
              ...getFrozenShoulderDataJson(),
              ...details,
            });

            // Status
            setStatus(p?.status || "Draft");
            if (p?.status === "Final") setReadOnly(true);
          }
        } catch (error) {
          showSnackbar("Error loading prescription: " + error.message, "error");
        }
      }
    };
    if (!readOnly) {
      loadPrescription();
      loadService();
    } 
    // else {
    //   loadService();
    // }
  }, [auth, serviceId, currentPrescriptionId]);

  let clinicalServiceCode = displayText(currentService?.serviceCode);
  let serviceName = displayText(currentService?.serviceName);

  let finalserviceName = clinicalServiceCode + " " + serviceName;

  const onRemovePrescriptionClick = async () => {
    if (auth && currentPrescriptionId) {
      try {
        const result = await removePrescription(auth, currentPrescriptionId);
        if (result?.isSuccess) {
          onRemovePrescription(serviceId, currentPrescriptionId);
          showSnackbar("Prescription removed successfully", "success");
        } else {
          showSnackbar(
            result?.data || "Failed to remove prescription",
            "error",
          );
        }
      } catch (error) {
        showSnackbar("Error removing prescription: " + error.message, "error");
      }
    }
  };

  const savePrescription = async (statusToSave) => {
    setIsSaving(true); // Add this line

    const prescription = {
      ...frozenShoulderData,
      prescriptionType: PrescriptionTypes.FrozenShoulder,
      status: statusToSave,
      availedServiceId: serviceId,
    };

   var result = {};
      if (!currentPrescriptionId) {
        result = await addPrescription(
          auth,
          prescription,
          currentService?.id,
          PrescriptionTypes.FrozenShoulder,
          statusToSave
        );
        if(result?.isSuccess){
          setCurrentPrescriptionId(result?.data);
        }
      } else {
        result = await updatePrescription(
          auth,
          prescription,
          currentPrescriptionId,
          statusToSave
        );
      }
      if (result.isSuccess) {
        setPrescriptionStatus(statusToSave);
        onSavePrescription(serviceId, currentPrescriptionId, statusToSave);
        showSnackbar(
          "The prescription has been saved successfully.",
          "success"
        );
      } else {
        showSnackbar(result?.data, "error");
      }
 };

  const renderDropdownRow = (label, value, setValue, options) => (
    <Grid2 container spacing={1} alignItems="center">
      <Grid2 size={{ xs: 12, sm: 4 }}>
        <Typography>{label}</Typography>
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 8 }}>
        <TextField
          select
          size="small"
          value={value}
          onChange={(e) => setValue(e.target.value)} // This already accepts a callback
          fullWidth
          disabled={readOnly}
        >
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      </Grid2>
    </Grid2>
  );

  if (readOnly || prescriptionStatus === "Final") {
    return (
      <FrozenShoulderPrescriptionReadOnly
        serviceId={serviceId}
        uniqueTabId={uniqueId}
        prescriptionId={currentPrescriptionId}
        changeTabsLabel={changeTabsLabel}
      />
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301, // One level above Dialog
          }}
        >
          {showLoadingImage()}
        </div>
      )}
      <Grid2 container size={12} spacing={2}>
        <Grid2 size={12}>
          <MenuBar
            showSave={true}
            onSave={() => savePrescription("Draft")}
            //saveDisabled={isPrescriptionEmpty()} // ADD THIS LINE
            showFinalize={true}
            onFinalize={() => savePrescription("Final")}
            //finalizeDisabled={isPrescriptionEmpty()} // ADD THIS LINE
            showDelete={prescriptionStatus === "Draft"}
            onDelete={onRemovePrescriptionClick} // UNCOMMENT THIS LINE
            disabled={isSaving} // UNCOMMENT THIS LINE
          />
        </Grid2>

        <Grid2 size={9}>
          <Card
            className="outerCard"
            variant="outlined"
            sx={{
              width: "100%",
              overflow: "auto",
              ml: "10px",
              padding: "10px",
            }}
          >
            <Grid2
              container
              size={12}
              spacing={2}
              sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
            >
              {/* Patient Information Card */}
              <Grid2 size={12}>
                <Card
                  sx={{
                    padding: { xs: 2, md: 3 },
                    pointerEvents: showLoading ? "none" : "auto",
                    opacity: showLoading ? 0.6 : 1,

                    color: "text.primary",
                    mb: 2,
                  }}
                  variant="outlined"
                >
                  <Grid2 container size={12} spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <HeaderValueDisplay
                        header="Patient Name"
                        value={
                          currentService?.patientPrefix +
                          " " +
                          currentService?.patientFirstName +
                          " " +
                          currentService?.patientLastName
                        }
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <HeaderValueDisplay
                        header="Age and Gender"
                        value={
                          formatAge(currentService?.patientAgeInYears) +
                          " and " +
                          currentService?.patientGender
                        }
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <HeaderValueDisplay
                        header="MRN"
                        value={displayText(currentService?.patientMRN)}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <HeaderValueDisplay
                        header="Visit Id"
                        value={displayText(currentService?.visitDisplayId)}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <HeaderValueDisplay
                        header="Visit Type"
                        value={displayText(currentService?.visitType)}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <HeaderValueDisplay
                        header="Visit Date Time"
                        value={convertUTCToTimeZoneDateTimeString(
                          new Date().toISOString(),
                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                        )}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <HeaderValueDisplay
                        header="Service Id"
                        value={currentService?.displayId}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <HeaderValueDisplay
                        header="Service Name"
                        value={finalserviceName}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                      <HeaderValueDisplay
                        header="Service Date Time"
                        value={convertUTCToTimeZoneDateTimeString(
                          new Date().toISOString(),
                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                        )}
                      />
                    </Grid2>
                  </Grid2>
                </Card>
              </Grid2>

              {/* Chief Complaint & HOPI */}
              <Grid2 size={12}>
                <Accordion defaultExpanded variant="outlined">
                  <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                    <Typography component="span">
                      Chief Complaint & HOPI
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "16px" }}>
                    <Grid2 container size={12} spacing={3}>
                      {/* Side */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Side",
                          frozenShoulderData.chiefComplaint.complaintSide,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                complaintSide: value,
                              },
                            }),
                          ["Right", "Left"],
                        )}
                      </Grid2>

                      {/* Duration */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <Grid2 container spacing={1} alignItems="center">
                          <Grid2 size={{ xs: 12, sm: 4 }}>
                            <Typography>Duration</Typography>
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 4 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={
                                frozenShoulderData.chiefComplaint
                                  .durationValue || ""
                              }
                              onChange={(e) =>
                                setFrozenShoulderData({
                                  ...frozenShoulderData,
                                  chiefComplaint: {
                                    ...frozenShoulderData.chiefComplaint,
                                    durationValue: e.target.value,
                                  },
                                })
                              }
                              fullWidth
                              disabled={readOnly}
                              placeholder="Enter duration"
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 4 }}>
                            <TextField
                              select
                              size="small"
                              value={
                                frozenShoulderData.chiefComplaint.durationUnit
                              }
                              onChange={(e) =>
                                setFrozenShoulderData({
                                  ...frozenShoulderData,
                                  chiefComplaint: {
                                    ...frozenShoulderData.chiefComplaint,
                                    durationUnit: e.target.value,
                                  },
                                })
                              }
                              fullWidth
                              disabled={readOnly}
                            >
                              <MenuItem value="Years">Years</MenuItem>
                              <MenuItem value="Months">Months</MenuItem>
                              <MenuItem value="Weeks">Weeks</MenuItem>
                              <MenuItem value="Days">Days</MenuItem>
                            </TextField>
                          </Grid2>
                        </Grid2>
                      </Grid2>

                      {/* Nature of Pain */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Nature of Pain",
                          frozenShoulderData.chiefComplaint.natureOfPain,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                natureOfPain: value,
                              },
                            }),
                          ["Continuous", "On Activity"],
                        )}
                      </Grid2>

                      {/* Symptoms */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Symptoms",
                          frozenShoulderData.chiefComplaint.symptoms,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                symptoms: value,
                              },
                            }),
                          ["Improving", "Worsening", "Remain Same"],
                        )}
                      </Grid2>

                      {/* Onset */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Onset",
                          frozenShoulderData.chiefComplaint.onset,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                onset: value,
                              },
                            }),
                          ["Gradual", "Sudden"],
                        )}
                      </Grid2>

                      {/* Injury */}
                      {/* <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Injury",
                          frozenShoulderData.chiefComplaint.injury,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                injury: value
                              }
                            }),
                          ["Yes", "No"]
                        )}
                      </Grid2> */}

                      {/* Type of Injury */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Type of Injury",
                          frozenShoulderData.chiefComplaint.typeOfInjury,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                typeOfInjury: value,
                              },
                            }),
                          ["Fall", "RTA", "Throwing", "Lifting", "Other"],
                        )}
                      </Grid2>

                      {/* Aggravating Factor */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Aggravating Factor",
                          frozenShoulderData.chiefComplaint.aggravatingFactor,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                aggravatingFactor: value,
                              },
                            }),
                          ["Any Movement of Shoulder Joint"],
                        )}
                      </Grid2>

                      {/* Relieving Factor */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Relieving Factor",
                          frozenShoulderData.chiefComplaint.relievingFactor,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                relievingFactor: value,
                              },
                            }),
                          [
                            "Rest",
                            "Medication",
                            "Icepad",
                            "Movement",
                            "Heatpad",
                            "Nothing",
                          ],
                        )}
                      </Grid2>

                      {/* Night Pain */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Night Pain",
                          frozenShoulderData.chiefComplaint.nightPain,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                nightPain: value,
                              },
                            }),
                          ["Increased", "Decreased"],
                        )}
                      </Grid2>

                      {/* Sleep Disturbance */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Sleep Disturbance",
                          frozenShoulderData.chiefComplaint.sleepDisturbance,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                sleepDisturbance: value,
                              },
                            }),
                          ["Yes", "No"],
                        )}
                      </Grid2>
                      <Grid2 size={12}>
                        <NotesTextField
                          value={frozenShoulderData.chiefComplaint.notes || ""}
                          onChange={(newValue) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              chiefComplaint: {
                                ...frozenShoulderData.chiefComplaint,
                                notes: newValue,
                              },
                            })
                          }
                          label="Chief Complaint Notes"
                          disabled={readOnly}
                          rows={3}
                          noteStatus={prescriptionStatus || "Draft"}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Grid2>

              {/* Past History */}
              <Grid2 size={12}>
                <Accordion variant="outlined">
                  <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                    <Typography component="span">Past History</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "16px" }}>
                    <Grid2 container size={12} spacing={3}>
                      {/* HTN */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "HTN",
                          frozenShoulderData.pastHistory.htn,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              pastHistory: {
                                ...frozenShoulderData.pastHistory,
                                htn: value,
                              },
                            }),
                          ["No", "Yes-Regular Meds", "Yes-Irregular Meds"],
                        )}
                      </Grid2>

                      {/* DM2 */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "DM2",
                          frozenShoulderData.pastHistory.dm2,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              pastHistory: {
                                ...frozenShoulderData.pastHistory,
                                dm2: value,
                              },
                            }),
                          [
                            "No",
                            "Yes-Regular Meds",
                            "Yes-Irregular Meds",
                            "Insulin",
                          ],
                        )}
                      </Grid2>

                      {/* Hypothyroid */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Hypothyroidism",
                          frozenShoulderData.pastHistory.hypothyroid,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              pastHistory: {
                                ...frozenShoulderData.pastHistory,
                                hypothyroid: value,
                              },
                            }),
                          ["No", "Yes-Regular Meds", "Yes-Irregular Meds"],
                        )}
                      </Grid2>

                      {/* Rx History */}
                      <Grid2 size={12}>
                        {/* <Typography fontWeight="bold" gutterBottom>
                          Rx History
                        </Typography> */}
                        <NotesTextField
                          value={frozenShoulderData.pastHistory.rxHistory || ""}
                          onChange={(newValue) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              pastHistory: {
                                ...frozenShoulderData.pastHistory,
                                rxHistory: newValue,
                              },
                            })
                          }
                          label="Enter prescription history"
                          disabled={readOnly}
                          rows={3}
                          noteStatus={prescriptionStatus}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Grid2>

              {/* On Examination */}
              <Grid2 size={12}>
                <Accordion variant="outlined">
                  <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                    <Typography component="span">On Examination</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "16px" }}>
                    <Grid2 container size={12} spacing={3}>
                      {/* Side Examined */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Side Examined",
                          frozenShoulderData.examination.examSide,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              examination: {
                                ...frozenShoulderData.examination,
                                examSide: value,
                              },
                            }),
                          ["Right", "Left"],
                        )}
                      </Grid2>

                      {/* Swelling */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Swelling",
                          frozenShoulderData.examination.swelling,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              examination: {
                                ...frozenShoulderData.examination,
                                swelling: value,
                              },
                            }),
                          ["Yes", "No"],
                        )}
                      </Grid2>

                      {/* Muscle Wasting */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Muscle Wasting",
                          frozenShoulderData.examination.muscleWasting,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              examination: {
                                ...frozenShoulderData.examination,
                                muscleWasting: value,
                              },
                            }),
                          ["Yes", "No"],
                        )}
                      </Grid2>

                      {/* Neurological Deficit */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        <Grid2 container spacing={1} alignItems="center">
                          <Grid2 size={{ xs: 12, sm: 4 }}>
                            <Typography>Neurological Deficit</Typography>
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 4 }}>
                            <TextField
                              select
                              size="small"
                              value={
                                frozenShoulderData.examination.neuroDeficit
                              }
                              onChange={(e) =>
                                setFrozenShoulderData({
                                  ...frozenShoulderData,
                                  examination: {
                                    ...frozenShoulderData.examination,
                                    neuroDeficit: e.target.value,
                                  },
                                })
                              }
                              fullWidth
                              disabled={readOnly}
                            >
                              <MenuItem value="Yes">Yes</MenuItem>
                              <MenuItem value="No">No</MenuItem>
                            </TextField>
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 4 }}>
                            <TextField
                              select
                              size="small"
                              value={
                                frozenShoulderData.examination.neuroDeficitType
                              }
                              onChange={(e) =>
                                setFrozenShoulderData({
                                  ...frozenShoulderData,
                                  examination: {
                                    ...frozenShoulderData.examination,
                                    neuroDeficitType: e.target.value,
                                  },
                                })
                              }
                              fullWidth
                              disabled={
                                readOnly ||
                                frozenShoulderData.examination.neuroDeficit ===
                                  "No"
                              }
                            >
                              <MenuItem value="Motor">Motor</MenuItem>
                              <MenuItem value="Sensory">Sensory</MenuItem>
                            </TextField>
                          </Grid2>
                        </Grid2>
                      </Grid2>

                      {/* Capsular Pattern Altered */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Capsular Pattern Altered",
                          frozenShoulderData.examination.capsularPattern,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              examination: {
                                ...frozenShoulderData.examination,
                                capsularPattern: value,
                              },
                            }),
                          ["Yes", "No"],
                        )}
                      </Grid2>

                      {/* Muscle Tightness */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Muscle Tightness",
                          frozenShoulderData.examination.muscleTightness,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              examination: {
                                ...frozenShoulderData.examination,
                                muscleTightness: value,
                              },
                            }),
                          ["Yes", "No"],
                        )}
                      </Grid2>

                      {/* Muscles Involved */}
                      <Grid2 size={12}>
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend">
                            Muscles Involved
                          </FormLabel>
                          <FormGroup>
                            <Grid2 container>
                              {muscleOptions.map((muscle) => (
                                <Grid2
                                  size={{ xs: 12, sm: 6, md: 4 }}
                                  key={muscle}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          frozenShoulderData.examination.musclesInvolved?.includes(
                                            muscle,
                                          ) || false
                                        }
                                        onChange={() => {
                                          const currentMuscles =
                                            frozenShoulderData.examination
                                              .musclesInvolved || [];
                                          const newMuscles =
                                            currentMuscles.includes(muscle)
                                              ? currentMuscles.filter(
                                                  (m) => m !== muscle,
                                                )
                                              : [...currentMuscles, muscle];
                                          setFrozenShoulderData({
                                            ...frozenShoulderData,
                                            examination: {
                                              ...frozenShoulderData.examination,
                                              musclesInvolved: newMuscles,
                                            },
                                          });
                                        }}
                                        disabled={readOnly}
                                      />
                                    }
                                    label={muscle}
                                  />
                                </Grid2>
                              ))}
                            </Grid2>
                          </FormGroup>
                        </FormControl>
                      </Grid2>

                      {/* ROM Table - Keep existing code, just update field access */}
                      <Grid2 size={12}>
                        <Typography fontWeight="bold" gutterBottom>
                          Range of Motion (ROM)
                        </Typography>
                        <Box sx={{ overflowX: "auto" }}>
                          <Table size="small" sx={{ minWidth: "600px" }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                  Movement
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  Normal ROM
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  Passive ROM (Left)
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  Passive ROM (Right)
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {frozenShoulderData.examination.rom.map(
                                (movement) => (
                                  <TableRow
                                    key={movement.name}
                                    sx={{
                                      "&:hover": {
                                        backgroundColor:
                                          "transparent !important",
                                      },
                                      "&.Mui-selected": {
                                        backgroundColor:
                                          "transparent !important",
                                      },
                                      "&.Mui-selected:hover": {
                                        backgroundColor:
                                          "transparent !important",
                                      },
                                    }}
                                  >
                                    <TableCell>
                                      {movement.displayName}
                                    </TableCell>
                                    <TableCell align="center">
                                      {movement.normal}°
                                    </TableCell>
                                    <TableCell align="center">
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={
                                          frozenShoulderData.examination.rom.find(
                                            (r) => r.name === movement.name,
                                          )?.left || ""
                                        }
                                        onChange={(e) => {
                                          setFrozenShoulderData({
                                            ...frozenShoulderData,
                                            examination: {
                                              ...frozenShoulderData.examination,
                                              rom: frozenShoulderData.examination.rom.map(
                                                (r) =>
                                                  r.name === movement.name
                                                    ? {
                                                        ...r,
                                                        left: e.target.value,
                                                      }
                                                    : r,
                                              ),
                                            },
                                          });
                                        }}
                                        disabled={readOnly}
                                        sx={{ width: "200px" }}
                                        placeholder="Enter value"
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={
                                          frozenShoulderData.examination.rom.find(
                                            (r) => r.name === movement.name,
                                          )?.right || ""
                                        }
                                        onChange={(e) => {
                                          setFrozenShoulderData({
                                            ...frozenShoulderData,
                                            examination: {
                                              ...frozenShoulderData.examination,
                                              rom: frozenShoulderData.examination.rom.map(
                                                (r) =>
                                                  r.name === movement.name
                                                    ? {
                                                        ...r,
                                                        right: e.target.value,
                                                      }
                                                    : r,
                                              ),
                                            },
                                          });
                                        }}
                                        disabled={readOnly}
                                        sx={{ width: "200px" }}
                                        placeholder="Enter value"
                                      />
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      </Grid2>

                      {/* Grip & Pinch */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Grip & Pinch",
                          frozenShoulderData.examination.gripPinch,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              examination: {
                                ...frozenShoulderData.examination,
                                gripPinch: value,
                              },
                            }),
                          ["Strong", "Weak"],
                        )}
                      </Grid2>

                      {/* Tone */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Tone",
                          frozenShoulderData.examination.tone,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              examination: {
                                ...frozenShoulderData.examination,
                                tone: value,
                              },
                            }),
                          ["Normal", "Hypertonic", "Hypotonic"],
                        )}
                      </Grid2>

                      {/* Coordination */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Coordination",
                          frozenShoulderData.examination.coordination,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              examination: {
                                ...frozenShoulderData.examination,
                                coordination: value,
                              },
                            }),
                          ["Good", "Moderate", "Poor"],
                        )}
                      </Grid2>
                      <Grid2 size={12}>
                        <NotesTextField
                          value={frozenShoulderData.examination.notes || ""}
                          onChange={(newValue) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              examination: {
                                ...frozenShoulderData.examination,
                                notes: newValue,
                              },
                            })
                          }
                          label="Examination Notes"
                          disabled={readOnly}
                          rows={3}
                          noteStatus={prescriptionStatus || "Draft"}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Grid2>

              {/* Special Tests */}
              <Grid2 size={12}>
                <Accordion variant="outlined">
                  <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                    <Typography component="span">Special Tests</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "16px" }}>
                    <Grid2 container size={12} spacing={3}>
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Thumb Drop Test",
                          frozenShoulderData.specialTests.thumbDropTest,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              specialTests: {
                                ...frozenShoulderData.specialTests,
                                thumbDropTest: value,
                              },
                            }),
                          ["Positive", "Negative"],
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "Painful Arc Test",
                          frozenShoulderData.specialTests.painfulArcTest,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              specialTests: {
                                ...frozenShoulderData.specialTests,
                                painfulArcTest: value,
                              },
                            }),
                          ["Positive", "Negative"],
                        )}
                      </Grid2>
                      <Grid2 size={12}>
                        <Grid2 size={12}>
                          <NotesTextField
                            value={frozenShoulderData.specialTests.notes || ""}
                            onChange={(newValue) =>
                              setFrozenShoulderData({
                                ...frozenShoulderData,
                                specialTests: {
                                  ...frozenShoulderData.specialTests,
                                  notes: newValue,
                                },
                              })
                            }
                            label="Special Tests Notes"
                            disabled={readOnly}
                            rows={3}
                            noteStatus={prescriptionStatus || "Draft"}
                          />
                        </Grid2>
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Grid2>

              {/* Functional Assessment */}
              <Grid2 size={12}>
                <Accordion variant="outlined">
                  <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                    <Typography component="span">
                      Functional Assessment
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "16px" }}>
                    <Grid2 container size={12} spacing={1}>
                      {/* ADL */}
                      <Grid2 size={{ xs: 12, md: 6 }}>
                        {renderDropdownRow(
                          "ADL",
                          frozenShoulderData.functionalAssessment.adl,
                          (value) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              functionalAssessment: {
                                ...frozenShoulderData.functionalAssessment,
                                adl: value,
                              },
                            }),
                          ["Dependent", "Independent"],
                        )}
                      </Grid2>

                      {/* Difficulties */}
                      <Grid2 size={12}>
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend">
                            Difficulty In
                          </FormLabel>
                          <FormGroup>
                            <Grid2 container>
                              {difficultyOptions.map((difficulty) => (
                                <Grid2
                                  size={{ xs: 12, sm: 6, md: 4 }}
                                  key={difficulty}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          frozenShoulderData.functionalAssessment.difficulties?.includes(
                                            difficulty,
                                          ) || false
                                        }
                                        onChange={() => {
                                          const currentDifficulties =
                                            frozenShoulderData
                                              .functionalAssessment
                                              .difficulties || [];
                                          const newDifficulties =
                                            currentDifficulties.includes(
                                              difficulty,
                                            )
                                              ? currentDifficulties.filter(
                                                  (d) => d !== difficulty,
                                                )
                                              : [
                                                  ...currentDifficulties,
                                                  difficulty,
                                                ];
                                          setFrozenShoulderData({
                                            ...frozenShoulderData,
                                            functionalAssessment: {
                                              ...frozenShoulderData.functionalAssessment,
                                              difficulties: newDifficulties,
                                            },
                                          });
                                        }}
                                        disabled={readOnly}
                                      />
                                    }
                                    label={difficulty}
                                  />
                                </Grid2>
                              ))}
                            </Grid2>
                          </FormGroup>
                        </FormControl>
                      </Grid2>
                      <Grid2 size={12}>
                        <NotesTextField
                          value={
                            frozenShoulderData.functionalAssessment.notes || ""
                          }
                          onChange={(newValue) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              functionalAssessment: {
                                ...frozenShoulderData.functionalAssessment,
                                notes: newValue,
                              },
                            })
                          }
                          label="Functional Assessment Notes"
                          disabled={readOnly}
                          rows={3}
                          noteStatus={prescriptionStatus || "Draft"}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Grid2>

              {/* Physiotherapy Management Plan */}
              <Grid2 size={12}>
                <Accordion variant="outlined">
                  <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                    <Typography component="span">
                      Physiotherapy Management Plan
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "16px" }}>
                    <Grid2 container size={12} spacing={3}>
                      {/* Modalities */}
                      <Grid2 size={12}>
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend">Modalities</FormLabel>
                          <FormGroup>
                            <Grid2 container>
                              {modalityOptions.map((modality) => (
                                <Grid2
                                  size={{ xs: 12, sm: 6, md: 4 }}
                                  key={modality}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          frozenShoulderData.managementPlan.modalities.includes(
                                            modality,
                                          ) || false
                                        }
                                        onChange={() => {
                                          const currentModalities =
                                            frozenShoulderData.managementPlan
                                              .modalities || [];
                                          const newModalities =
                                            currentModalities.includes(modality)
                                              ? currentModalities.filter(
                                                  (m) => m !== modality,
                                                )
                                              : [
                                                  ...currentModalities,
                                                  modality,
                                                ];
                                          setFrozenShoulderData({
                                            ...frozenShoulderData,
                                            managementPlan: {
                                              ...frozenShoulderData.managementPlan,
                                              modalities: newModalities,
                                            },
                                          });
                                        }}
                                        disabled={readOnly}
                                      />
                                    }
                                    label={modality}
                                  />
                                </Grid2>
                              ))}
                            </Grid2>
                          </FormGroup>
                        </FormControl>
                      </Grid2>

                      {/* Exercise Plan */}
                      <Grid2 size={12}>
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend">
                            Exercise Plan
                          </FormLabel>
                          <FormGroup>
                            <Grid2 container>
                              {exerciseOptions.map((exercise) => (
                                <Grid2
                                  size={{ xs: 12, sm: 6, md: 4 }}
                                  key={exercise}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          frozenShoulderData.managementPlan.exercisePlan.includes(
                                            exercise,
                                          ) || false
                                        }
                                        onChange={() => {
                                          const currentExercises =
                                            frozenShoulderData.managementPlan
                                              .exercisePlan || [];
                                          const newExercises =
                                            currentExercises.includes(exercise)
                                              ? currentExercises.filter(
                                                  (e) => e !== exercise,
                                                )
                                              : [...currentExercises, exercise];
                                          setFrozenShoulderData({
                                            ...frozenShoulderData,
                                            managementPlan: {
                                              ...frozenShoulderData.managementPlan,
                                              exercisePlan: newExercises,
                                            },
                                          });
                                        }}
                                        disabled={readOnly}
                                      />
                                    }
                                    label={exercise}
                                  />
                                </Grid2>
                              ))}
                            </Grid2>
                          </FormGroup>
                        </FormControl>
                      </Grid2>

                      {/* Prognosis */}
                      <Grid2 size={12}>
                        {/* <Typography fontWeight="bold" gutterBottom>
                          Prognosis (in Patient's Words)
                        </Typography> */}
                        <NotesTextField
                          value={
                            frozenShoulderData.managementPlan.prognosis || ""
                          }
                          onChange={(newValue) =>
                            setFrozenShoulderData({
                              ...frozenShoulderData,
                              managementPlan: {
                                ...frozenShoulderData.managementPlan,
                                prognosis: newValue,
                              },
                            })
                          }
                          label="Enter prognosis details"
                          disabled={readOnly}
                          rows={3}
                          noteStatus={prescriptionStatus}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Grid2>
            </Grid2>
          </Card>
        </Grid2>
      </Grid2>
    </Box>
  );
}

export default FrozenShoulderPrescriptionForm;
